export const MC_NEXT_ARCHITECTURE_PIPELINE = [
  {
    id: "individual-engagement-dmo",
    title: "Individual & Engagement DMOs",
    icon: "database",
    color: "#e879f9",
    summary: "MC Next runs directly on Data Cloud's Data Model Objects — no separate contact database, no Data Extensions.",
    details: [
      "The Individual DMO holds each person's core identity/profile fields, while the Engagement DMO captures behavioral events like cart abandonment, clicks, or app opens.",
      "Unlike classic Marketing Cloud, there is no import/sync step — MC Next reads and writes directly against these DMOs inside Data Cloud, so every channel shares one live source of truth.",
      "Any new engagement source (POS, mobile SDK, web event) just streams into the Engagement DMO and is instantly usable by every downstream MC Next capability.",
    ],
  },
  {
    id: "identity-resolution-mcn",
    title: "Identity Resolution",
    icon: "fingerprint",
    color: "#38bdf8",
    summary: "Match & Reconciliation Rules stitch fragmented records into one Unified Individual before any marketing action happens.",
    details: [
      "Before a segment or flow can target 'a customer', Identity Resolution must first resolve every source record for that person into a single Unified Individual using Match Rules.",
      "Reconciliation Rules decide which source wins when the same field (e.g., email) conflicts across systems.",
      "Every downstream MC Next feature — segmentation, agentic personalization, distributed sends — assumes this resolved identity already exists.",
    ],
  },
  {
    id: "business-unit-data-space",
    title: "Business Unit & Data Space Partitioning",
    icon: "building",
    color: "#f59e0b",
    summary: "Multi-brand orgs isolate marketing data per Business Unit using Data Space partitions on the same Data Cloud instance.",
    details: [
      "A Data Space partitions Data Cloud records so each Business Unit only sees and activates its own slice of data, even though everyone shares one org.",
      "Consent & Preference records are checked per BU — a contact opted-in for Brand A isn't automatically marketable under Brand B.",
      "This replaces the old model of spinning up entirely separate Marketing Cloud Business Units just for data isolation.",
    ],
  },
  {
    id: "segment-builder-mcn",
    title: "Segment Builder",
    icon: "filter",
    color: "#34d399",
    summary: "Segments are built straight from Individual + Engagement DMOs — no Automation Studio import step required.",
    details: [
      "MC Next's Segment Builder queries Individual, Engagement, and Calculated Insight DMOs directly, combining attribute and behavioral criteria in one rule builder.",
      "Because segmentation reads live DMO data, membership reflects near real-time behavior instead of yesterday's imported file.",
      "A published segment is scoped to its Business Unit's Data Space, keeping brand-level targeting isolated automatically.",
    ],
  },
  {
    id: "event-driven-flow",
    title: "Event-Driven Flow",
    icon: "zap",
    color: "#fb7185",
    summary: "Fires the moment a qualifying event lands in Data Cloud — cart abandonment, CRM stage change — with no batch delay.",
    details: [
      "An Event-Driven Flow triggers in near real-time off an Engagement DMO event or a CRM signal (like an Opportunity moving to Closed-Won), instead of waiting for a nightly batch job.",
      "This is the direct successor to Journey Builder's entry sources, but grounded in Data Cloud events rather than a single Data Extension import.",
      "Flows can branch on identity-resolved attributes and Calculated Insights, giving each individual a personalized path.",
    ],
  },
  {
    id: "agentforce-personalization",
    title: "Agentforce Personalization",
    icon: "bot",
    color: "#a78bfa",
    summary: "An AI agent drafts the actual message content — grounded in the Unified Individual's real Data Cloud profile.",
    details: [
      "Agentforce reads the resolved Unified Individual profile plus recent engagement to generate personalized subject lines, copy, and offers — not a static template with merge fields.",
      "For B2B or rep-facing flows, a human Approval Gate can sit between AI generation and send, letting a rep review before anything goes out.",
      "This is what makes MC Next 'agentic' — personalization logic lives in an AI agent grounded on live data, not in a hand-authored AMPscript block.",
    ],
  },
  {
    id: "distributed-activation",
    title: "Distributed & BU-Scoped Activation",
    icon: "rocket",
    color: "#22d3ee",
    summary: "The final send respects consent, Business Unit sender identity, and — for reps — sends from the individual's own identity.",
    details: [
      "Activation delivers the Agentforce-personalized content through the correct channel, using only that Business Unit's sender profile and deliverability settings.",
      "Distributed Marketing sends can originate from an individual rep's own identity (e.g., a sales rep's thank-you note) rather than a single corporate blast.",
      "Consent status is enforced at this final gate — an unresolved or opted-out individual is excluded even if they matched the segment.",
    ],
  },
];
